/**
 * HeroBackground Component
 *
 * Animated PCB (Printed Circuit Board) background for Partix Hero section.
 * Features:
 * - Realistic circuit board traces with animated signal pulses
 * - Hardware component representations (chips, resistors, capacitors)
 * - Subtle glow effects using Partix green branding (#00A76F)
 * - Optimized SVG animations for smooth performance
 * - Fully independent component for easy import/export/commenting
 *
 * Design Philosophy:
 * - Represents hardware development (PCB = physical hardware)
 * - Subtle but engaging (won't compete with hero content)
 * - Professional, technical aesthetic
 * - Green color scheme matches Partix brand identity
 */

import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

import { varAlpha } from '../../../../theme/palette';
import { alpha } from '@mui/material/styles';

// Use simple Stack instead of MotionContainer
// import { MotionContainer } from '../../../components/animate';

// Placeholder for Dots, Lines, etc., since we don't have hero-svg
// We'll create simple placeholders

const Dots = () => (
  <Box
    component="svg"
    width="100%"
    height="100%"
    viewBox="0 0 100 100"
    sx={{ position: 'absolute', top: 0, left: 0 }}
  >
    <circle cx="20" cy="20" r="2" fill="var(--hero-circle-stroke-color)" />
    <circle cx="80" cy="30" r="2" fill="var(--hero-circle-stroke-color)" />
    <circle cx="50" cy="70" r="2" fill="var(--hero-circle-stroke-color)" />
  </Box>
);

const Texts = () => (
  <Box sx={{ position: 'absolute', top: '20%', left: '10%', opacity: 0.1 }}>
    
  </Box>
);

const Circles = () => (
  <g>
    {/* <circle cx="200" cy="200" r="100" stroke="var(--hero-circle-stroke-color)" strokeWidth="var(--hero-circle-stroke-width)" fill="none" />
    <circle cx="1200" cy="300" r="80" stroke="var(--hero-circle-stroke-color)" strokeWidth="var(--hero-circle-stroke-width)" fill="none" /> */}
  </g>
);

const PlusIcon = () => (
  <g>
    <line x1="400" y1="150" x2="450" y2="150" stroke="var(--hero-plus-stroke-color)" strokeWidth="2" />
    <line x1="425" y1="125" x2="425" y2="175" stroke="var(--hero-plus-stroke-color)" strokeWidth="2" />
  </g>
);

const Lines = ({ strokeCount }) => (
  <g>
    {Array.from({ length: strokeCount }, (_, i) => (
      <line
        key={i}
        x1={100 + i * 100}
        y1={100}
        x2={100 + i * 100}
        y2={900}
        stroke="var(--hero-line-stroke-color)"
        strokeWidth="var(--hero-line-stroke-width)"
        strokeDasharray="var(--stroke-dasharray)"
      />
    ))}
  </g>
);

export default function HeroBackground({ sx }) {
  const theme = useTheme();

  const lightMode = theme.palette.mode === 'light';

  const strokeCount = 12;

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        '--stroke-dasharray': 3,
        '--stroke-spacing': '80px',
        /* line */
        '--hero-line-stroke-width': 1,
        '--hero-line-stroke-color': lightMode
          ? varAlpha(theme.palette.grey['500Channel'], 0.32)
          : varAlpha(theme.palette.grey['600Channel'], 0.16),
        /* text */
        '--hero-text-stroke-width': 1,
        '--hero-text-stroke-color': lightMode
          ? varAlpha(theme.palette.grey['500Channel'], 0.24)
          : varAlpha(theme.palette.grey['600Channel'], 0.12),
        /* circle */
        '--hero-circle-stroke-width': 1,
        '--hero-circle-stroke-color': lightMode
          ? varAlpha(theme.palette.grey['500Channel'], 0.48)
          : varAlpha(theme.palette.grey['600Channel'], 0.24),
        /* plus */
        '--hero-plus-stroke-color': theme.palette.text.disabled,
        top: 0,
        left: 0,
        width: 1,
        height: 1,
        position: 'absolute',
        ...sx,
      }}
    >
      <Dots />

      <Texts />

      <Box
        component={m.svg}
        xmlns="http://www.w3.org/2000/svg"
        width="1440"
        height="1080"
        fill="none"
        viewBox="0 0 1440 1080"
        initial="hidden"
        animate="visible"
        sx={{ width: 1, height: 1 }}
      >
        <defs>
          <radialGradient
            id="mask_gradient_id"
            cx="0"
            cy="0"
            r="1"
            gradientTransform="matrix(720 0 0 420 720 560)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity={1} />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity={0.08} />
          </radialGradient>

          <mask id="mask_id">
            <ellipse cx="50%" cy="50%" rx="50%" ry="36%" fill="url(#mask_gradient_id)" />
          </mask>
        </defs>

        <g mask="url(#mask_id)">
          <Circles />
          <PlusIcon />
          <Lines strokeCount={strokeCount} />
        </g>
      </Box>

      {/* <Box
        component={m.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        sx={{
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          zIndex: -1,
          position: 'absolute',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundImage:
            theme.palette.mode === 'light'
              ? `linear-gradient(180deg, ${theme.palette.background.default} 12%, ${alpha(theme.palette.background.default, 0.92)} 50%, ${theme.palette.background.default} 88%), url('/assets/background/background-3.webp')`
              : `url('/assets/images/home/hero-blur.webp'), linear-gradient(180deg, ${theme.palette.background.default} 12%, ${alpha(theme.palette.background.default, 0.96)} 50%, ${theme.palette.background.default} 88%), url('/assets/background/background-3.webp')`,
        }}
      /> */}
    </Stack>
  );
}
